import { Alert, Button, Checkbox, Empty, Select, Skeleton, Table, Tag, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import IntegrationCheckResult from '../../components/integration-check-result/IntegrationCheckResult';
import { apiService } from '../../services/api';
import { formatApiErrorDetail } from '../../utils/formatApiErrorDetail';
import { canCheck, canEnable, exportStatus, installationLabel, serverLabel } from './journalS3';

// SM2. «Подключения → Журналы S3»: по каждому подключению — что за сервер и что с его выгрузкой журнала
// в общий бакет; «Включить выгрузку» по одному и пачкой (ключ — из «Интеграций → Журналы S3»), «Проверить»
// на сервере, установки из бакета и ручная привязка установок снятых серверов.

const TONE_COLOR = { ok: 'green', warn: 'orange', off: 'default', none: 'default' };

const pick = (response) => response?.data?.data ?? null;

const ConnectionsJournalS3 = () => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState({});
  const [error, setError] = useState(null);
  const [bindChoice, setBindChoice] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setView(pick(await apiService.getJournalS3Connections()));
    } catch (e) {
      setError(formatApiErrorDetail(e) || 'Не удалось получить состояние серверов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => view?.connections || [], [view]);
  const enableable = useMemo(() => rows.filter((row) => canEnable(row.probe)).map((row) => row.id), [rows]);

  const scan = async () => {
    setScanning(true);
    setError(null);
    try {
      setView(pick(await apiService.refreshJournalS3()));
      message.success('Бакет обойдён');
    } catch (e) {
      setError(formatApiErrorDetail(e) || 'Обход бакета не удался');
    } finally {
      setScanning(false);
    }
  };

  const enable = async (ids) => {
    if (!ids.length) return;
    setEnabling(true);
    try {
      const data = pick(await apiService.enableJournalS3Export(ids));
      const got = data?.results || {};
      setResults((prev) => {
        const next = { ...prev };
        Object.entries(got).forEach(([id, result]) => { next[id] = { result }; });
        return next;
      });
      const okCount = Object.values(got).filter((result) => result?.ok).length;
      message[okCount === ids.length ? 'success' : 'warning'](`Выгрузка включена: ${okCount} из ${ids.length}`);
      await load();
    } catch (e) {
      message.error(formatApiErrorDetail(e) || 'Ошибка включения выгрузки');
    } finally {
      setEnabling(false);
    }
  };

  const check = async (id) => {
    setCheckingId(id);
    try {
      const result = pick(await apiService.checkJournalS3Connection(id));
      setResults((prev) => ({ ...prev, [id]: { result } }));
    } catch (e) {
      setResults((prev) => ({ ...prev, [id]: { error: formatApiErrorDetail(e) || 'Ошибка проверки' } }));
    } finally {
      setCheckingId(null);
    }
  };

  const bind = async (uuid) => {
    const connectionId = bindChoice[uuid];
    if (!connectionId) return;
    try {
      await apiService.bindJournalS3Installation(uuid, connectionId);
      message.success('Установка привязана');
      await load();
    } catch (e) {
      message.error(formatApiErrorDetail(e) || 'Ошибка привязки');
    }
  };

  const toggle = (id, on) => setSelected((prev) => (on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));

  const columns = [
    {
      title: (
        <Checkbox
          aria-label="Выбрать все серверы Midori"
          checked={enableable.length > 0 && selected.length === enableable.length}
          indeterminate={selected.length > 0 && selected.length < enableable.length}
          onChange={(e) => setSelected(e.target.checked ? enableable : [])}
        />
      ),
      key: 'select',
      width: 48,
      render: (_, row) => (
        <Checkbox
          aria-label={`Выбрать ${row.name}`}
          disabled={!canEnable(row.probe)}
          checked={selected.includes(row.id)}
          onChange={(e) => toggle(row.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Подключение',
      key: 'name',
      render: (_, row) => (
        <div>
          <div>{row.name}</div>
          <div className="sett-cell--muted">{row.server_url}</div>
        </div>
      ),
    },
    { title: 'Сервер', key: 'server', render: (_, row) => serverLabel(row.probe) },
    {
      title: 'Выгрузка',
      key: 'export',
      render: (_, row) => {
        const status = exportStatus(row.probe);
        return <Tag color={TONE_COLOR[status.tone]} style={{ whiteSpace: 'normal' }}>{status.text}</Tag>;
      },
    },
    {
      title: 'Установки в бакете',
      key: 'installations',
      render: (_, row) => (row.installations.length ? (
        <div>
          {row.installations.map((item) => (
            <div key={item.installation_uuid}>
              {installationLabel(item)}
              {item.conflict && <Tag color="orange" style={{ marginLeft: 6, whiteSpace: 'normal' }}>⚠ {item.conflict}</Tag>}
            </div>
          ))}
        </div>
      ) : <span className="sett-cell--muted">—</span>),
    },
    {
      title: '',
      key: 'actions',
      width: 230,
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            size="small"
            type="primary"
            disabled={!canEnable(row.probe) || enabling}
            onClick={() => enable([row.id])}
            aria-label={`Включить выгрузку ${row.name}`}
          >
            Включить выгрузку
          </Button>
          <Button
            size="small"
            disabled={!canCheck(row.probe)}
            loading={checkingId === row.id}
            onClick={() => check(row.id)}
            aria-label={`Проверить выгрузку ${row.name}`}
          >
            Проверить
          </Button>
        </div>
      ),
    },
  ];

  const unbound = view?.unbound_installations || [];
  const connectionOptions = rows.map((row) => ({ value: row.id, label: row.name }));

  return (
    <div className="conn-tab-content">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Журнал доступа серверов Midori раз в сутки уходит в общий бакет S3"
        description={
          'Ключ — из «Настройки → Proxy Manager → Интеграции → Журналы S3». «Включить выгрузку» сохраняет его на сервере и '
          + 'проверяет хранилище с сервера; выключенную выгрузку включает только после удачной проверки. Папка установки '
          + 'в бакете — из «Названия стенда» сервера.'
        }
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Button onClick={load} loading={loading}>Обновить</Button>
        <Button onClick={scan} loading={scanning}>Обойти бакет</Button>
        <Button type="primary" disabled={!selected.length} loading={enabling} onClick={() => enable(selected)}>
          {`Включить выгрузку на выбранных (${selected.length})`}
        </Button>
        {view?.bucket_scanned_at && (
          <span className="sett-cell--muted" style={{ alignSelf: 'center' }}>
            {`бакет обойдён: ${new Date(view.bucket_scanned_at).toLocaleString()}`}
          </span>
        )}
      </div>
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}
      {loading && !view ? (
        <Skeleton active />
      ) : (
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={rows}
          columns={columns}
          locale={{ emptyText: <Empty description="Активных подключений нет" /> }}
          expandable={{
            expandedRowKeys: Object.keys(results).map(Number),
            showExpandColumn: false,
            expandedRowRender: (row) => (
              <IntegrationCheckResult
                title={row.name}
                result={results[row.id]?.result}
                error={results[row.id]?.error}
              />
            ),
          }}
        />
      )}
      {unbound.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3>Установки без подключения</h3>
          <p className="sett-cell--muted">
            Сервер снят или ещё не опрошен. Привяжите установку к подключению вручную — её журнал будет искаться вместе с ним.
          </p>
          {unbound.map((item) => (
            <div key={item.installation_uuid} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <span>{installationLabel(item)}</span>
              {item.conflict && <Tag color="orange" style={{ whiteSpace: 'normal' }}>⚠ {item.conflict}</Tag>}
              <Select
                size="small"
                style={{ minWidth: 200 }}
                placeholder="Подключение"
                options={connectionOptions}
                value={bindChoice[item.installation_uuid]}
                onChange={(value) => setBindChoice((prev) => ({ ...prev, [item.installation_uuid]: value }))}
                aria-label={`Подключение для ${item.installation_name}`}
              />
              <Button size="small" disabled={!bindChoice[item.installation_uuid]} onClick={() => bind(item.installation_uuid)}>
                Привязать
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConnectionsJournalS3;
